import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";
import { Box, TextField, FormControlLabel, Switch, Button, Typography, Paper } from "@mui/material";
import Swal from "sweetalert2";
import { fetchAllBGM } from "../../actions/backgroundMusicActions";
import { addObjectTag, removeObjectTag } from "../../actions/tagActions";
import { fetchAuthors } from "../../actions/authorsActions";
import { clearMedia, uploadImage, uploadQuickTapVideo } from "../../actions/mediaActions";
import {
  addEditQuickTapSession,
  deleteQuickTapSession,
  fetchOneQuickTapSession,
} from "../../actions/quickTapsActions";
import { fetchAllCategories } from "../../actions/categoryActions";
import { fetchAllSubCategories } from "../../actions/subcategoryActions";
import { fetchTappingPoints } from "../../actions/tappingPointsActions";
import TappingPointsTimestamp from "../AddEditSession/TappingPointsTimestamp/TappingPointsTimestamp";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import ImageContainerPreview from "../Common/ImageContainerPreview";
import AuthorSelector from "../Common/AuthorSelector";
import ParentSelector from "../Common/ParentSelector";
import BackgroundMusicSelector from "../Common/BackgroundMusicSelector";
import TagsSection from "../Common/TagsSection";
import VideoContainerPreview from "../Common/VideoContainerPreview";
import { preventEnterFormSubmission } from "../../utils/formUtils";
import UniversalLinkField from "../Common/UniversalLinkField";

const selectMedia = createSelector([state => state.media], media => ({
  image: media.image,
  video: media.video,
}));

const AddEditQuickTapSession = ({ isEdit, selectedId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Ref to access TappingPointsTimestamp methods
  const timestampComponentRef = useRef(null);

  // Redux selectors
  const tapping_points = useSelector(state => state.tapping_points.tapping_points);
  const { image: quick_tap_image, video } = useSelector(selectMedia);
  const background_musics = useSelector(state => state.background_musics.background_musics);

  // Loading and error state
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [error, setError] = useState(null);

  // Local state
  const [formData, setFormData] = useState({
    quick_tap_title: "",
    quick_tap_description: "",
    quick_tap_is_new: 0,
    quick_tap_is_featured: 0,
    quick_tap_is_premium: 0,
    quick_tap_is_active: 1,
    quick_tap_is_dark_mode: 0,
    quick_tap_video_playback_id: "",
    quick_tap_deep_link_image: "",
    quick_tap_created_at: "",
    quick_tap_updated_at: "",
    quick_tap_bg_music_url: "",
    quick_tap_image: "",
    quick_tap_duration: "",
    quick_tap_duration_in_sec: 0,
    quick_tap_author_id: "",
    video_url_preview: "",
    video_uploading: false,
    image_preview: "",
    image_uploading: false,
    deep_link_image_preview: "",
    deep_link_image_uploading: false,
    session_bgm_id: "0",
    bg_timestamps: [{ tapping_point_id: "", timestamp: 0 }],
    tags: [],
    associatedParent: "0",
    category_id: 0,
    subcategory_id: 0,
  });

  // Data loading
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        dispatch(clearMedia());
        await Promise.all([
          dispatch(fetchAllCategories(1)),
          dispatch(fetchAllSubCategories(0, 1)),
          dispatch(fetchAuthors()),
          dispatch(fetchTappingPoints()),
          dispatch(fetchAllBGM()),
        ]);
        if (isEdit) {
          setIsLoadingSession(true);
          try {
            const sessionData = await dispatch(fetchOneQuickTapSession(selectedId));
            if (sessionData) {
              let associatedParent = "0";
              if (sessionData.category?.category_id) {
                associatedParent = sessionData.category.category_id + "CAT";
              } else if (sessionData.subcategory?.subcategory_id) {
                associatedParent = sessionData.subcategory.subcategory_id + "SUB";
              }

              const bgmId = background_musics?.find(bgm => bgm.bgm_url === sessionData.bg_music_url)?.bgm_id || "0";

              setFormData(prev => ({
                ...prev,
                quick_tap_id: sessionData.id,
                quick_tap_title: sessionData.title,
                quick_tap_description: sessionData.description || "",
                quick_tap_is_new: sessionData.is_new,
                quick_tap_is_featured: sessionData.is_featured,
                quick_tap_is_premium: sessionData.is_premium,
                quick_tap_is_active: sessionData.is_active,
                quick_tap_is_dark_mode: sessionData.is_dark_mode,
                quick_tap_video_playback_id: sessionData.video_playback_id || "",
                quick_tap_duration: sessionData.duration || "",
                quick_tap_duration_in_sec: sessionData.duration_in_sec || 0,
                quick_tap_deep_link_image: sessionData.deep_link_image || "",
                universal_link: sessionData.universal_link || "",
                quick_tap_created_at: sessionData.created_at || "",
                quick_tap_updated_at: sessionData.updated_at || "",
                quick_tap_bg_music_url: sessionData.bg_music_url || "",
                quick_tap_image: sessionData.image || "",
                quick_tap_author_id: sessionData.author_id || "",
                bg_timestamps: sessionData.bg_timestamps?.length > 0
                  ? sessionData.bg_timestamps
                  : [{ tapping_point_id: "", timestamp: 0 }],
                tags: sessionData.tags || [],
                associatedParent,
                session_bgm_id: String(bgmId),
              }));
            } else {
              setError("Failed to load quick tap data.");
            }
          } catch (err) {
            setError("Failed to load quick tap data.");
          } finally {
            setIsLoadingSession(false);
          }
        }
        setIsLoading(false);
      } catch (err) {
        setError("Failed to load required data. Please try again.");
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, isEdit, selectedId]);

  // BGM matching effect - update session_bgm_id when background_musics or bg_music_url become available
  useEffect(() => {
    if (isEdit && background_musics?.length > 0 && formData.quick_tap_bg_music_url) {
      const bgmId = background_musics.find(bgm => bgm.bgm_url === formData.quick_tap_bg_music_url)?.bgm_id || "0";
      if (String(bgmId) !== formData.session_bgm_id) {
        setFormData(prev => ({ ...prev, session_bgm_id: String(bgmId) }));
      }
    }
  }, [background_musics, isEdit, formData.quick_tap_bg_music_url]);

  // Handle image upload
  const handleImageUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.image_preview) {
      URL.revokeObjectURL(formData.image_preview);
    }
    setFormData(prev => ({ ...prev, image_preview: URL.createObjectURL(file), image_uploading: true }));
    await dispatch(uploadImage(file));
  };

  // Handle deep link image upload
  const handleDeepLinkImageUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.deep_link_image_preview) {
      URL.revokeObjectURL(formData.deep_link_image_preview);
    }
    setFormData(prev => ({
      ...prev,
      deep_link_image_preview: URL.createObjectURL(file),
      deep_link_image_uploading: true,
    }));
    await dispatch(uploadImage(file));
  };

  // Handle video upload
  const handleVideoUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.video_url_preview) {
      URL.revokeObjectURL(formData.video_url_preview);
    }
    setFormData(prev => ({ ...prev, video_url_preview: URL.createObjectURL(file), video_uploading: true }));
    await dispatch(uploadQuickTapVideo(file));
  };

  // Update image/deep link image/video after upload
  useEffect(() => {
    if (quick_tap_image && formData.image_uploading) {
      setFormData(prev => ({ ...prev, quick_tap_image: quick_tap_image, image_uploading: false }));
      dispatch(clearMedia());
    }
    if (quick_tap_image && formData.deep_link_image_uploading) {
      setFormData(prev => ({ ...prev, quick_tap_deep_link_image: quick_tap_image, deep_link_image_uploading: false }));
      dispatch(clearMedia());
    }
    if (video && formData.video_uploading) {
      setFormData(prev => ({
        ...prev,
        video_uploading: false,
        quick_tap_video_playback_id: video.playback_id,
        quick_tap_duration: video.duration,
        quick_tap_duration_in_sec: video.duration_in_sec,
      }));
      dispatch(clearMedia());
    }
  }, [
    quick_tap_image,
    video,
    dispatch,
    formData.image_uploading,
    formData.deep_link_image_uploading,
    formData.video_uploading,
  ]);

  // Handlers
  const handleFieldChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckBox = e => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
  };

  // Timestamps
  const handleTimestampChange = (value, stateProp, index, field) => {
    setFormData(prev => ({
      ...prev,
      bg_timestamps: prev.bg_timestamps.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addTimestamp = () => {
    const newIndex = formData.bg_timestamps.length;
    setFormData(prev => {
      const newTimestamps = [...prev.bg_timestamps, { tapping_point_id: "", timestamp: 0 }];
      return {
        ...prev,
        bg_timestamps: newTimestamps,
      };
    });

    // Focus on the new timestamp field using the component ref
    if (timestampComponentRef.current) {
      timestampComponentRef.current.focusOnNewItem(newIndex);
    }
  };

  const removeTimestamp = index => {
    setFormData(prev => ({
      ...prev,
      bg_timestamps: prev.bg_timestamps.filter((_, i) => i !== index),
    }));
  };

  const handleImportTimestamps = importedTimestamps => {
    setFormData(prev => ({
      ...prev,
      bg_timestamps: [
        ...prev.bg_timestamps,
        ...importedTimestamps.map(ts => ({
          tapping_point_id: ts.tapping_point_id,
          timestamp: ts.timestamp,
          image_url: ts.image_url,
        })),
      ],
    }));
  };

  // Tags
  const handleTagsChange = newTags => {
    setFormData(prev => ({ ...prev, tags: newTags }));
    // Tags are now auto-applied via TagsSection component
  };

  // Submission
  const handleSubmit = async e => {
    e.preventDefault();
    Swal.fire({
      title: "Saving...",
      animation: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });
    if (!formData.quick_tap_author_id) {
      Swal.fire({ icon: "error", text: "You must select an Author for this session." });
      return;
    }
    let { bg_timestamps: timestamps_json, ...quick_tap_session_json } = formData;
    timestamps_json = timestamps_json.filter(obj => obj.tapping_point_id !== "");
    const times = timestamps_json.map(obj => obj.timestamp);
    const uniqueTimes = [...new Set([...times])];
    if (times.length !== uniqueTimes.length) {
      Swal.fire({ icon: "error", text: "Cannot have two tapping points at same time." });
      return;
    }
    let category_id = 0;
    let subcategory_id = 0;
    if (formData.associatedParent.indexOf("CAT") > -1) {
      category_id = parseInt(formData.associatedParent, 10);
    } else if (formData.associatedParent.indexOf("SUB") > -1) {
      subcategory_id = parseInt(formData.associatedParent, 10);
    }
    quick_tap_session_json.quick_tap_updated_at = null;
    quick_tap_session_json.quick_tap_created_at = null;
    const options = {
      quick_tap_session_json: JSON.stringify(quick_tap_session_json),
      timestamps_json: JSON.stringify(timestamps_json),
      category_id,
      subcategory_id,
    };
    await dispatch(addEditQuickTapSession(options, () => navigate('/quick-taps')));
    // Tags are now auto-applied via TagsSection component
    // No need for manual tag handling here
  };

  // Render options
  const renderBGMOptions = useMemo(() => {
    if (!background_musics) return [];

    const options = background_musics.map(obj => ({
      label: obj.bgm_title,
      value: String(obj.bgm_id),
      quick_tap_bg_music_url: obj.bgm_url,
    }));
    const none = { label: "None", value: "0", quick_tap_bg_music_url: "" };
    return [none, ...options];
  }, [background_musics]);

  // Loading and error states
  if (isLoading || isLoadingSession) {
    return <LoadingBackdrop open={true} />;
  }
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button onClick={() => navigate("/quick-taps")}>Return to Quick Taps</Button>
      </Box>
    );
  }

  // Main form
  return (
    <Box
      component="form"
      id="quick-tap-form"
      onSubmit={handleSubmit}
      onKeyDown={preventEnterFormSubmission}
      autoComplete="off"
    >
      <Box display="grid" gap={4}>
        {/* Basic Information */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={3}>
            <TextField
              fullWidth
              label="Quick Tap Session Name"
              name="quick_tap_title"
              value={formData.quick_tap_title}
              onChange={handleFieldChange}
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="quick_tap_description"
              value={formData.quick_tap_description}
              onChange={handleFieldChange}
              multiline
              rows={3}
              placeholder="Enter a description for this Quick Tap session"
            />
            <AuthorSelector
              value={formData.quick_tap_author_id}
              onChange={value => setFormData(prev => ({ ...prev, quick_tap_author_id: value }))}
              required
            />
            <ParentSelector
              value={formData.associatedParent}
              onChange={value => setFormData(prev => ({ ...prev, associatedParent: value }))}
              allowedTypes={["category", "subcategory"]}
              showQuickTaps={true}
              label="Parent Category or Subcategory"
            />
          </Box>
        </Box>
        {/* Status and Flags */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Quick Tap Settings
          </Typography>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }} gap={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.quick_tap_is_active }
                  onChange={handleCheckBox}
                  name="quick_tap_is_active"
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch checked={formData.quick_tap_is_new } onChange={handleCheckBox} name="quick_tap_is_new" />
              }
              label="Is New"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.quick_tap_is_featured }
                  onChange={handleCheckBox}
                  name="quick_tap_is_featured"
                />
              }
              label="Featured"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.quick_tap_is_premium }
                  onChange={handleCheckBox}
                  name="quick_tap_is_premium"
                />
              }
              label="Premium"
            />
          </Box>
        </Box>
        {/* Media and Sharing Settings Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Media & Sharing Settings
          </Typography>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 2fr" }} gap={4}>
            {/* Quick Tap Image */}
            <Box>
              <ImageContainerPreview
                title="Quick Tap Image"
                image={formData.image_preview || formData.quick_tap_image}
                name="quick_tap_image"
                handleRemove={() => {
                  if (formData.image_preview) URL.revokeObjectURL(formData.image_preview);
                  setFormData(prev => ({ ...prev, quick_tap_image: "", image_preview: "" }));
                }}
                handleImageUpload={handleImageUpload}
                showCopyUrlButton={true}
                sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
              />
            </Box>

            {/* Deep Link Image */}
            <Box>
              <ImageContainerPreview
                title="Deep Link Image"
                image={formData.deep_link_image_preview || formData.quick_tap_deep_link_image}
                name="quick_tap_deep_link_image"
                handleRemove={() => {
                  if (formData.deep_link_image_preview) URL.revokeObjectURL(formData.deep_link_image_preview);
                  setFormData(prev => ({ ...prev, quick_tap_deep_link_image: "", deep_link_image_preview: "" }));
                }}
                handleImageUpload={handleDeepLinkImageUpload}
                showCopyUrlButton={true}
                sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
              />
            </Box>

            {/* Universal Link */}
            <Box>
              <UniversalLinkField url={formData.universal_link} />
            </Box>
          </Box>
        </Box>

        {/* Tags Section */}
        {isEdit && (
          <Box>
            <TagsSection
              tags={formData.tags}
              onTagsChange={handleTagsChange}
              objectId={formData.quick_tap_id}
              objectType="QuickTap"
            />
          </Box>
        )}

        {/* Video and Background Music Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Video & Background Music
          </Typography>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
            {/* Video */}
            <Box>
              <Box display="flex" flexDirection="column" gap={2}>
                <VideoContainerPreview
                  title="Quick Tap Video"
                  video={formData.quick_tap_video_playback_id}
                  name="quick_tap_video_playback_id"
                  handleRemove={() => {
                    if (formData.video_url_preview) URL.revokeObjectURL(formData.video_url_preview);
                    setFormData(prev => ({
                      ...prev,
                      quick_tap_video_playback_id: "",
                      video_url_preview: "",
                      quick_tap_duration: "",
                      quick_tap_duration_in_sec: 0,
                    }));
                  }}
                  handleVideoUpload={handleVideoUpload}
                  showCopyUrlButton={true}
                  sx={{ height: "100%" }}
                />
              </Box>
            </Box>

            {/* Background Music */}
            <Box>
              <BackgroundMusicSelector
                title="Background Music"
                options={renderBGMOptions}
                selectedValue={formData.session_bgm_id}
                audioUrl={formData.quick_tap_bg_music_url}
                isLoading={isLoading}
                onSelectionChange={newValue => {
                  setFormData(prev => ({
                    ...prev,
                    session_bgm_id: newValue ? newValue.value : "0",
                    quick_tap_bg_music_url: newValue ? newValue.quick_tap_bg_music_url : "",
                  }));
                }}
                onRemove={() => {
                  setFormData(prev => ({
                    ...prev,
                    session_bgm_id: "0",
                    quick_tap_bg_music_url: "",
                  }));
                }}
                showCopyUrlButton={true}
                showLockToggle={false}
                sx={{ height: "100%" }}
              />
            </Box>
          </Box>
        </Box>
        {/* Tapping Points Timings */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Tapping Points Timings
          </Typography>
          <Paper sx={{ p: 2 }}>
            <TappingPointsTimestamp
              ref={timestampComponentRef}
              dense
              items={formData.bg_timestamps}
              removeTimestamp={removeTimestamp}
              stateProp="bg_timestamps"
              handleTimestampChange={(value, stateProp, index, field) =>
                handleTimestampChange(value, stateProp, index, field)
              }
              tappingPoints={tapping_points}
              onImportTimestamps={handleImportTimestamps}
              maxDurationSeconds={formData.quick_tap_duration_in_sec || null}
            />
            <Button variant="contained" onClick={addTimestamp} sx={{ mt: 2 }} data-add-timestamp-button>
              Add Timestamp
            </Button>
          </Paper>
        </Box>
        {/* Archive/Delete Button */}
        {isEdit && (
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                if (formData.quick_tap_is_active ) {
                  Swal.fire({
                    icon: "error",
                    text: "Can't delete this Quick Tap, please update this Quick Tap to inactive status before deleting",
                  });
                  return;
                }
                const result = await Swal.fire({
                  icon: "warning",
                  text: "Are you sure you want to delete this Quick Tap?",
                  showConfirmButton: true,
                  showCancelButton: true,
                  reverseButtons: true,
                  confirmButtonText: "Yes, delete!",
                });
                if (result.value) {
                  dispatch(deleteQuickTapSession(formData.quick_tap_id, () => navigate("/quick-taps")));
                }
              }}
            >
              Delete Quick Tap
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AddEditQuickTapSession;
